/**
 * A-08 現場・原価管理（FR-124・D-71・D-73）＝**現場ごとの材料費が、発注した時点でわかる**。
 * 工務店LP 特長03 の実体。
 *
 * ★ **集計はクライアント側**（D-71）＝接続している卸を順に照会して合算する。サーバに
 *   横断集計の API を作らないのは、テナント分離（RLS）を壊さないため＝1社ぶんずつしか
 *   引けないのが正しい形で、「複数の卸にまたがる1つの現場」は職人の側の概念だから。
 * ★ 金額は**注文に保存済みのスナップショット**を使う（04 §5）。クライアントで単価を
 *   計算し直さない＝画面と注文で金額がズレる3つ目の実装を作らない。
 * ★ **「原価」は材料費（税抜・契約単価）の意味**。人件費・外注費は含まない（画面に明記）。
 *   LC-05（請求情報は職人に出さない）とは別物＝これは**自分が出した発注の集計**であって
 *   卸からの請求額ではない。
 */

import type { ConnectedTenant, PartnerOrder, partnerApi, SiteBudget } from "@kenzai/api-client";
import { Card, ErrorNote, Skeleton } from "@kenzai/ui";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { PageTitle } from "../Shell";
import { useIsDesktop } from "../useIsDesktop";
import { OrdersSegments } from "./Orders";

/**
 * 見出し（M1.9b）＝どの状態（ロード中・空・通常）でも同じ頭を出す。
 *
 * ★**スマホはセグメントを出さない**＝注文履歴側の3分割（すべて／現場ごと／卸ごと）と
 *   役割が重なり、同じ帯が2種類の意味を持ってしまう。この画面への入口はプロフィールに置く。
 * ★**PC は Design どおりセグメントを出す**（すべての注文 ⇄ 現場・原価管理）＝PC の注文履歴は
 *   表なので3分割が無く、帯の意味が1つに定まる。**`/sites` への唯一の導線**でもある。
 */
function SiteCostsHeader({ isDesktop }: { isDesktop: boolean }) {
  if (isDesktop) {
    return (
      <div>
        <div className="kp-head">
          <h1>現場・原価管理</h1>
          <p>お取引先すべての発注を、現場ごとに合計しています（税抜・材料費のみ）。</p>
        </div>
        <div style={{ marginBottom: "var(--kz-space-4)" }}>
          <OrdersSegments on="sites" />
        </div>
      </div>
    );
  }
  return <PageTitle>現場・原価管理</PageTitle>;
}

type Client = ReturnType<typeof partnerApi>;

/** 納品済み＝実績。取消は数えない。それ以外（受付済み〜配送中）は「発注残」。 */
const DELIVERED = "納品済み";
const CANCELLED = "取消";

// 予算の消化率がここを超えたら注意色（デモ『発注アプリ_PC_工務店向け.html』の定義＝D-43）
const WARN_RATIO = 0.9;

type Row = {
  site: string;
  delivered: number;
  outstanding: number;
  byTenant: Map<string, number>;
  byCategory: Map<string, number>;
  budget: number | null;
};

function yen(n: number): string {
  return `¥${Math.round(n).toLocaleString("ja-JP")}`;
}

function aggregate(
  perTenant: { tenant: ConnectedTenant; orders: PartnerOrder[]; budgets: SiteBudget[] }[],
): Row[] {
  const rows = new Map<string, Row>();
  const get = (site: string): Row => {
    let r = rows.get(site);
    if (!r) {
      r = { site, delivered: 0, outstanding: 0, byTenant: new Map(), byCategory: new Map(), budget: null };
      rows.set(site, r);
    }
    return r;
  };

  for (const { tenant, orders, budgets } of perTenant) {
    for (const o of orders) {
      if (o.status === CANCELLED) continue;
      // 現場が未設定の注文は「現場未設定」にまとめる（黙って落とすと合計が合わない）
      const r = get(o.site_name ?? "（現場の指定なし）");
      const total = Number(o.total_amount ?? 0);
      if (o.status === DELIVERED) r.delivered += total;
      else r.outstanding += total;
      r.byTenant.set(tenant.tenant_name, (r.byTenant.get(tenant.tenant_name) ?? 0) + total);
      for (const i of o.items) {
        const key = i.category ?? "その他";
        r.byCategory.set(key, (r.byCategory.get(key) ?? 0) + Number(i.amount ?? 0));
      }
    }
    // ★予算は**取引先ごと**に持つ（D-73）＝同じ現場に複数の卸がいれば合計して見せる
    for (const b of budgets) {
      const r = get(b.site_label);
      r.budget = (r.budget ?? 0) + Number(b.amount);
    }
  }
  return [...rows.values()].sort((a, b) => b.delivered + b.outstanding - (a.delivered + a.outstanding));
}

export function SiteCosts({ client, connections }: { client: Client; connections: ConnectedTenant[] }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  // PC は Design の KPI ＋ 表（スマホのカードとは排他＝同じ testid を二重に出さない）
  const isDesktop = useIsDesktop();

  // ★停止中の卸は注文API も予算API も 403（03 §8）＝**引きに行かない**。
  //   引くと1社の停止でこの画面がまるごとエラーになる（合算する画面なので巻き添えが広い）。
  //   M1.5 までは Shell が停止中の卸で本文ごと塞いでいたので表に出なかったが、
  //   M1.6（D-83）で全画面ブロックをやめた＝ここで受け止める。欠けている旨は下に出す。
  const usable = connections.filter((c) => c.tenant_status !== "suspended");
  const suspended = connections.filter((c) => c.tenant_status === "suspended");

  const orderQueries = useQueries({
    queries: usable.map((c) => ({
      queryKey: ["orders", c.tenant_id],
      queryFn: () => client.orders(c.tenant_id),
    })),
  });
  const budgetQueries = useQueries({
    queries: usable.map((c) => ({
      queryKey: ["budgets", c.tenant_id],
      queryFn: () => client.budgets(c.tenant_id),
    })),
  });

  const loading = [...orderQueries, ...budgetQueries].some((q) => q.isLoading);
  const failed = [...orderQueries, ...budgetQueries].find((q) => q.isError);

  const rows = useMemo(
    () =>
      aggregate(
        usable.map((tenant, i) => ({
          tenant,
          orders: orderQueries[i]?.data?.items ?? [],
          budgets: budgetQueries[i]?.data?.items ?? [],
        })),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useQueries の配列は毎回新しい参照になる
    [connections, orderQueries.map((q) => q.dataUpdatedAt).join(), budgetQueries.map((q) => q.dataUpdatedAt).join()],
  );

  /**
   * 今月の材料費（納品済み・税抜）＝PC の KPI ②。
   * ★**「いつ納品されたか」で数える**＝納品の事実（timeline）が正。timeline に納品の段が
   *   無い注文（古い注文・段の名前が変わった場合）はお届け予定日で代用する＝黙って 0 にしない。
   */
  const deliveredThisMonth = useMemo(() => {
    const now = new Date();
    const key = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    let sum = 0;
    for (const q of orderQueries) {
      for (const o of q.data?.items ?? []) {
        if (o.status !== DELIVERED) continue;
        const at = o.timeline.find((t) => t.step === DELIVERED)?.at ?? o.desired_date;
        if (at && at.slice(0, 7) === key) sum += Number(o.total_amount ?? 0);
      }
    }
    return sum;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useQueries の配列は毎回新しい参照になる
  }, [orderQueries.map((q) => q.dataUpdatedAt).join()]);

  // ★合算画面＝**全卸ぶんが揃うまで待つ**構造は不変（途中経過の合計を出すと金額が動く）。
  //   ロード中に `data-testid="site-costs"` を出さない（E2E が「見えた＝合計が確定」で読む）。
  if (loading) {
    return (
      <>
        <SiteCostsHeader isDesktop={isDesktop} />
        <SiteCostsSkeleton />
      </>
    );
  }
  if (failed) {
    return (
      <>
        <SiteCostsHeader isDesktop={isDesktop} />
        <ErrorNote error={failed.error} />
      </>
    );
  }

  const total = rows.reduce((s, r) => s + r.delivered + r.outstanding, 0);

  return (
    <div style={{ display: "grid", gap: "var(--kz-space-4)" }} data-testid="site-costs">
      <SiteCostsHeader isDesktop={isDesktop} />
      {/* ★見出しは `PageTitle`（＝他の画面と同じ場所・同じ大きさ）に一本化した。
          ここには**同じ文言の `<h1>` がもう1枚**あり、`h1` が1画面に2つ並んでいた
          （セグメントが間に挟まっていたので気づきにくかった）。リード文だけ残す。
          ★PC は同じ説明を `kp-head` が持ち、数字は下の KPI が持つ＝二重に書かない。 */}
      {!isDesktop && (
        <p style={{ color: "var(--kz-color-ink-muted)", fontSize: "var(--kz-font-size-sm)", margin: 0 }}>
          お取引先すべての発注を、現場ごとに合計しています。合計 <strong>{yen(total)}</strong>
        </p>
      )}

      {isDesktop && rows.length > 0 && <SiteKpis rows={rows} total={total} thisMonth={deliveredThisMonth} />}

      <ErrorNote error={error} />

      {suspended.map((c) => (
        <p
          key={c.tenant_id}
          style={{ margin: 0, fontSize: "var(--kz-font-size-sm)", color: "var(--kz-status-danger)" }}
        >
          {c.tenant_name} は現在ご利用いただけないため、この卸の発注は合計に入っていません。
        </p>
      ))}

      {rows.length === 0 ? (
        <Card>まだ発注がありません。注文するとここに現場ごとの材料費が出ます。</Card>
      ) : isDesktop ? (
        <SiteTable
          rows={rows}
          open={open}
          onToggle={(site) => setOpen(open === site ? null : site)}
          detail={(r) => (
            <SiteDetail
              client={client}
              // 停止中の卸は予算も保存できない（403）＝選べる相手から外す
              connections={usable}
              row={r}
              withBars
              onDone={() => {
                setError(null);
                void qc.invalidateQueries({ queryKey: ["budgets"] });
              }}
              onError={setError}
            />
          )}
        />
      ) : (
        rows.map((r) => (
          <Card key={r.site} padding="0">
            <div
              data-testid={`site-row-${r.site}`}
              style={{ padding: "var(--kz-space-4)", display: "grid", gap: "var(--kz-space-2)" }}
            >
              <div style={{ display: "flex", alignItems: "baseline", gap: "var(--kz-space-3)", flexWrap: "wrap" }}>
                <strong style={{ fontSize: "var(--kz-font-size-lg)" }}>{r.site}</strong>
                <span style={{ marginLeft: "auto", fontWeight: 700 }}>{yen(r.delivered + r.outstanding)}</span>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "var(--kz-space-4)",
                  flexWrap: "wrap",
                  fontSize: "var(--kz-font-size-sm)",
                  color: "var(--kz-color-ink-muted)",
                }}
              >
                {/* 納品済＝実績／発注残＝受付〜配送中（デモの定義＝D-43） */}
                <span>
                  納品済み <span className="kz-num">{yen(r.delivered)}</span>
                </span>
                <span>
                  発注残 <span className="kz-num">{yen(r.outstanding)}</span>
                </span>
              </div>

              <BudgetBar row={r} />

              <button
                type="button"
                data-testid={`site-detail-${r.site}`}
                onClick={() => setOpen(open === r.site ? null : r.site)}
                className="kz-pressable"
                style={{
                  minHeight: 44,
                  border: "1px solid var(--kz-color-border)",
                  borderRadius: "var(--kz-radius-sm)",
                  background: "var(--kz-color-surface)",
                  font: "inherit",
                  cursor: "pointer",
                }}
              >
                {open === r.site ? "内訳を閉じる" : "内訳を見る"}
              </button>

              {open === r.site && (
                <SiteDetail
                  client={client}
                  // 停止中の卸は予算も保存できない（403）＝選べる相手から外す
                  connections={usable}
                  row={r}
                  onDone={() => {
                    setError(null);
                    void qc.invalidateQueries({ queryKey: ["budgets"] });
                  }}
                  onError={setError}
                />
              )}
            </div>
          </Card>
        ))
      )}

      <p style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)", margin: 0 }}>
        {/* LC-05 との区別＝これは請求額ではない。人件費・外注費も含まない。 */}
        ここに出るのは<strong>材料費（税抜・お取引の単価）</strong>だけです。人件費・外注費は含みません。
        <br />
        お支払いの金額は、お取引先の建材卸売業者さまからのご請求をご確認ください。
      </p>
    </div>
  );
}

/** 読み込み中の現場・原価（M1.7）＝見出し＋現場カードの形で待たせる（素の帯だけ）。 */
function SiteCostsSkeleton() {
  return (
    <div aria-hidden="true" style={{ display: "grid", gap: "var(--kz-space-4)" }}>
      <div style={{ display: "grid", gap: "var(--kz-space-2)" }}>
        <div className="kz-skeleton" style={{ height: 26, width: "48%" }} />
        <div className="kz-skeleton" style={{ height: 14, width: "72%" }} />
      </div>
      {[0, 1, 2].map((i) => (
        <Card key={i} padding="var(--kz-space-4)">
          <div style={{ display: "flex", gap: "var(--kz-space-3)" }}>
            <div className="kz-skeleton" style={{ height: 20, width: "42%" }} />
            <div className="kz-skeleton" style={{ height: 20, width: 96, marginLeft: "auto" }} />
          </div>
          <div style={{ marginTop: "var(--kz-space-3)" }}>
            <Skeleton rows={2} height={14} />
          </div>
          <div className="kz-skeleton" style={{ height: 8, borderRadius: 999, marginTop: "var(--kz-space-3)" }} />
        </Card>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* PC（Design『発注アプリ_PC_工務店向け』の KPI ＋ 現場テーブル）          */
/* ------------------------------------------------------------------ */

/**
 * KPI 4枚（Design の cols-4）。
 *
 * ★Design の①は「進行中の現場」だが、**現場に状態（進行中／完工）を持っていない**
 *   （D-73＝現場は「注文が返す現場名＋取引先ごとの予算」だけ）。数えられない数を
 *   それらしく出すより、**数えられるもの＝発注実績のある現場の数**に置き換える。
 * ★④は Design の「予算の注意現場」＝**予算を入れてくれた現場だけ**が対象。入れていない
 *   現場を「注意なし」に数えると、予算管理をしていないことが「順調」に見える。
 */
function SiteKpis({ rows, total, thisMonth }: { rows: Row[]; total: number; thisMonth: number }) {
  const outstanding = rows.reduce((s, r) => s + r.outstanding, 0);
  const watched = rows.filter((r) => r.budget !== null && r.budget > 0);
  const alerts = watched.filter((r) => (r.delivered + r.outstanding) / (r.budget ?? 1) >= WARN_RATIO);
  const month = new Date().getMonth() + 1;

  const cards: { label: string; value: string; foot: string; alert?: boolean }[] = [
    { label: "発注のある現場", value: `${rows.length}件`, foot: `発注の合計 ${yen(total)}` },
    { label: "今月の材料費（納品済み）", value: yen(thisMonth), foot: `${month}月・全現場・税抜` },
    { label: "発注残（未納品）", value: yen(outstanding), foot: "受付済み〜配送中のご注文" },
    {
      label: "予算の注意が要る現場",
      value: `${alerts.length}件`,
      foot:
        alerts.length > 0
          ? `${alerts[0].site} が予算の${WARN_RATIO * 100}%超え`
          : watched.length === 0
            ? "実行予算はまだ登録されていません"
            : `実行予算を入れた${watched.length}件を見ています`,
      alert: alerts.length > 0,
    },
  ];

  return (
    <div className="kp-kpis">
      {cards.map((c) => (
        <Card key={c.label} className={c.alert ? "kpi kp-alert" : "kpi"} padding="var(--kz-space-4)">
          <span className="label">{c.label}</span>
          <span className="value">{c.value}</span>
          <span className="foot">{c.foot}</span>
        </Card>
      ))}
    </div>
  );
}

/**
 * 現場の表（Design の現場テーブル）。
 *
 * ★**列の並びが Design と違う**（Design＝納品済／発注残／合計、こちら＝合計／納品済／発注残）。
 *   理由は2つ：①行の見出しの次に来るべきは「この現場にいくら出したか」＝合計
 *   ②`site-row-*` の**最初の金額を合計として読む**検査（E2E site-costs.spec）が既にあり、
 *   納品前の現場（納品済み ¥0）で先頭が 0 になると、消化率の検査が成り立たない。
 * ★Design の「ステータス（進行中/完工）」「期間」「工程」の列は**持っていないデータ**なので
 *   作らない（D-73）。空の列を並べると「入れ忘れ」に見える。
 */
function SiteTable({
  rows,
  open,
  onToggle,
  detail,
}: {
  rows: Row[];
  open: string | null;
  onToggle: (site: string) => void;
  detail: (row: Row) => React.ReactNode;
}) {
  return (
    <div>
      <div className="kp-tblwrap">
        <table className="kp-tbl">
          <thead>
            <tr>
              <th>現場</th>
              <th className="num">合計（税抜）</th>
              <th className="num">内訳</th>
              <th>予算の消化</th>
              <th aria-label="内訳の開閉" />
            </tr>
          </thead>
          {rows.map((r) => {
            const isOpen = open === r.site;
            return (
              <tbody key={r.site}>
                <tr
                  data-testid={`site-row-${r.site}`}
                  className={`clickable${isOpen ? " kp-open" : ""}`}
                  onClick={() => onToggle(r.site)}
                >
                  <td>
                    <b>{r.site}</b>
                    {r.budget !== null && r.budget > 0 && <span className="sub">実行予算 {yen(r.budget)}</span>}
                  </td>
                  <td className="num">
                    <b>{yen(r.delivered + r.outstanding)}</b>
                  </td>
                  {/* ★語（納品済み／発注残）は**セルの中に残す**＝表の見出しは上に1行しか無く、
                      現場が増えるとスクロールで見えなくなる。列見出しに預けると「この数字は
                      どっちだったか」を上に戻って確かめることになる。 */}
                  <td className="num">
                    <span className="sub">納品済み {yen(r.delivered)}</span>
                    <span className="sub">発注残 {yen(r.outstanding)}</span>
                  </td>
                  <td>
                    <BudgetCell row={r} />
                  </td>
                  <td>
                    <button
                      type="button"
                      data-testid={`site-detail-${r.site}`}
                      className="kp-chev"
                      aria-expanded={isOpen}
                      aria-label={`${r.site} の内訳を${isOpen ? "閉じる" : "見る"}`}
                      onClick={(e) => {
                        // 行にも同じ開閉が付いている＝止めないと2回走って開かない
                        e.stopPropagation();
                        onToggle(r.site);
                      }}
                    >
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2.2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  </td>
                </tr>
                {isOpen && (
                  <tr className="kp-items">
                    <td colSpan={5}>{detail(r)}</td>
                  </tr>
                )}
              </tbody>
            );
          })}
        </table>
      </div>
      <p className="kp-key" style={{ marginTop: 10 }}>
        <span className="kp-key">
          <i className="k-d" />
          納品済み（実績）
        </span>
        <span className="kp-key">
          <i className="k-p" />
          発注残（受付済み〜配送中）
        </span>
      </p>
    </div>
  );
}

/**
 * 表の「予算の消化」セル＝**納品済み（ベタ）＋発注残（斜線）**の2色バー（Design `.kp-bar`）。
 * 実行予算を入れていない現場は「—」＝率を出しようがないので、0% と書かない。
 * ★testid・％の文言（超過／注意）はスマホの `BudgetBar` と同じ＝どちらの版でも同じ読み方。
 */
function BudgetCell({ row }: { row: Row }) {
  if (row.budget === null || row.budget <= 0) {
    return <span style={{ color: "var(--kz-color-ink-faint)" }}>—</span>;
  }
  const used = row.delivered + row.outstanding;
  const ratio = used / row.budget;
  const over = ratio >= 1;
  const warn = ratio >= WARN_RATIO;
  const pct = (n: number) => `${Math.min(100, (n / (row.budget ?? 1)) * 100)}%`;
  return (
    <span className="kp-usage" data-testid={`budget-bar-${row.site}`}>
      <span className={`kp-bar sm${over ? " over" : warn ? " warn" : ""}`}>
        <i className="kp-bar-d" style={{ width: pct(row.delivered) }} />
        <i
          className="kp-bar-p"
          style={{ width: `${Math.max(0, Math.min(100, (used / row.budget) * 100) - (row.delivered / row.budget) * 100)}%` }}
        />
      </span>
      <b style={{ color: over ? "var(--kz-status-danger)" : warn ? "var(--kz-status-warn)" : "inherit" }}>
        {Math.round(ratio * 100)}%{over ? "（超過）" : warn ? "（注意）" : ""}
      </b>
    </span>
  );
}

/**
 * 現場の内訳（スマホのカードの中／PC の展開行で**同じもの**を出す）。
 * ★PC だけ構成比のバーを添える（`withBars`）＝広い画面では数字の並びより形の方が速い。
 */
function SiteDetail({
  client,
  connections,
  row,
  withBars = false,
  onDone,
  onError,
}: {
  client: Client;
  connections: ConnectedTenant[];
  row: Row;
  withBars?: boolean;
  onDone: () => void;
  onError: (e: unknown) => void;
}) {
  // ★PC は Design の2カラム（左＝内訳を読む／右＝予算を入れる・CSV を出す）。
  //   1カラムのまま横に伸ばすと、入力欄が 1,200px の帯になって「どこに何桁入れるのか」が
  //   分からなくなる（実測）。
  const body = (
    <>
      <div style={{ display: "grid", gap: "var(--kz-space-3)", minWidth: 0 }}>
        <Breakdown title="取引先ごと" map={row.byTenant} withBars={withBars} />
        <Breakdown title="種類ごと" map={row.byCategory} withBars={withBars} />
      </div>
      <div style={{ display: "grid", gap: "var(--kz-space-3)", minWidth: 0, alignContent: "start" }}>
        <BudgetForm
          client={client}
          connections={connections}
          site={row.site}
          current={row.budget}
          onDone={onDone}
          onError={onError}
        />
        <ExportButton row={row} />
      </div>
    </>
  );

  return withBars ? (
    <div className="kp-two">{body}</div>
  ) : (
    <div style={{ display: "grid", gap: "var(--kz-space-3)" }}>{body}</div>
  );
}

function BudgetBar({ row }: { row: Row }) {
  if (row.budget === null || row.budget <= 0) return null;
  const used = row.delivered + row.outstanding;
  const ratio = used / row.budget;
  const over = ratio >= 1;
  const warn = ratio >= WARN_RATIO;
  return (
    <div data-testid={`budget-bar-${row.site}`}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "var(--kz-font-size-sm)" }}>
        <span>実行予算 {yen(row.budget)}</span>
        <span
          style={{ fontWeight: 700, color: over ? "var(--kz-status-danger)" : warn ? "var(--kz-status-warn)" : "inherit" }}
        >
          {Math.round(ratio * 100)}%
          {over ? "（超過）" : warn ? "（注意）" : ""}
        </span>
      </div>
      <div style={{ height: 8, background: "var(--kz-color-surface-sunken)", borderRadius: 999 }}>
        <div
          style={{
            width: `${Math.min(100, ratio * 100)}%`,
            height: "100%",
            borderRadius: 999,
            background: over
              ? "var(--kz-status-danger)"
              : warn
                ? "var(--kz-status-warn)"
                : "var(--kz-color-primary)",
          }}
        />
      </div>
    </div>
  );
}

function Breakdown({
  title,
  map,
  withBars = false,
}: {
  title: string;
  map: Map<string, number>;
  /** PC＝Design の構成比バーを添える（幅は**その内訳の中の最大値**を基準にする）。 */
  withBars?: boolean;
}) {
  const rows = [...map.entries()].sort((a, b) => b[1] - a[1]);
  if (rows.length === 0) return null;
  const max = rows[0][1];

  if (withBars) {
    return (
      <div>
        <div style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)", marginBottom: 6 }}>
          {title}
        </div>
        <div className="kp-cats">
          {rows.map(([k, v]) => (
            <div className="kp-cat" key={k}>
              <span className="kp-cat-n" title={k}>
                {k}
              </span>
              <span className="kp-cat-bar">
                {/* max が 0（全部 0 円）なら棒は出さない＝0 除算で NaN% を書き込まない */}
                <i style={{ width: max > 0 ? `${(v / max) * 100}%` : 0 }} />
              </span>
              <span className="kp-cat-v">{yen(v)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>{title}</div>
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
          <span>{k}</span>
          <span className="kz-num">{yen(v)}</span>
        </div>
      ))}
    </div>
  );
}

function BudgetForm({
  client,
  connections,
  site,
  current,
  onDone,
  onError,
}: {
  client: Client;
  connections: ConnectedTenant[];
  site: string;
  current: number | null;
  onDone: () => void;
  onError: (e: unknown) => void;
}) {
  const [value, setValue] = useState(current ? String(current) : "");
  const [tenantId, setTenantId] = useState(connections[0]?.tenant_id ?? 0);
  const [busy, setBusy] = useState(false);

  return (
    <div style={{ display: "grid", gap: "var(--kz-space-2)" }}>
      <div style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
        実行予算（材料費の枠）
        {/* ★D-73 の代償を画面で正直に言う＝現場は卸のマスタなので、予算も取引先ごとに持つ */}
        {connections.length > 1 && <>／お取引先ごとに設定します（合計で表示します）</>}
      </div>
      {connections.length > 1 && (
        <select
          data-testid={`budget-tenant-${site}`}
          value={tenantId}
          onChange={(e) => setTenantId(Number(e.target.value))}
          style={{ minHeight: 44, font: "inherit", padding: "var(--kz-space-2)" }}
        >
          {connections.map((c) => (
            <option key={c.tenant_id} value={c.tenant_id}>
              {c.tenant_name}
            </option>
          ))}
        </select>
      )}
      <div style={{ display: "flex", gap: "var(--kz-space-2)", flexWrap: "wrap" }}>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          data-testid={`budget-input-${site}`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="例）3000000"
          style={{ minHeight: 44, minWidth: 160, flex: 1, font: "inherit", padding: "var(--kz-space-2)" }}
        />
        <button
          type="button"
          data-testid={`budget-save-${site}`}
          className="kz-pressable"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            client
              .putBudget(tenantId, site, value.trim() === "" ? null : value.trim())
              .then(onDone, onError)
              .finally(() => setBusy(false));
          }}
          style={{
            minHeight: 44,
            padding: "0 var(--kz-space-4)",
            border: "none",
            borderRadius: "var(--kz-radius-sm)",
            background: "var(--kz-color-primary)",
            color: "var(--kz-color-ink-inverse)",
            font: "inherit",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          保存
        </button>
      </div>
    </div>
  );
}

function ExportButton({ row }: { row: Row }) {
  return (
    <button
      type="button"
      data-testid={`site-csv-${row.site}`}
      className="kz-pressable"
      onClick={() => {
        const lines = [
          "現場,区分,名称,金額",
          ...[...row.byTenant.entries()].map(([k, v]) => `"${row.site}",取引先,"${k}",${Math.round(v)}`),
          ...[...row.byCategory.entries()].map(([k, v]) => `"${row.site}",種類,"${k}",${Math.round(v)}`),
          `"${row.site}",合計,納品済み,${Math.round(row.delivered)}`,
          `"${row.site}",合計,発注残,${Math.round(row.outstanding)}`,
        ];
        // ★BOM 付き＝Excel が UTF-8 と判別できず日本語が化けるのを防ぐ（卸の CSV と同じ作法）
        const blob = new Blob(["\ufeff", `${lines.join("\r\n")}\r\n`], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${row.site}_材料費.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }}
      style={{
        minHeight: 44,
        border: "1px solid var(--kz-color-border)",
        borderRadius: "var(--kz-radius-sm)",
        background: "var(--kz-color-surface)",
        font: "inherit",
        cursor: "pointer",
      }}
    >
      この現場の明細を CSV で保存
    </button>
  );
}
